import {
    CurrencyUtil, DisplayType, GeneralSettings, Keys, Navigation, StringUtil, UrlUtil
} from "@mcleod/core";
import {
    ClickEvent, Component, DataSourceAction, DataSourceExecutionEvent, DataSourceMode,
    ForcedCase, Label, LookupModelSelectionEvent, Panel, Tab, TableRow, TableRowDisplayEvent
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { LMECompanySettings, LMEGeneralSettings } from "@mcleod/general";
import { AutogenLayoutOrderList } from "./autogen/AutogenLayoutOrderList";
import { PlanningUtil } from "./planning/PlanningUtil";
import { OrderTabView } from "./OrderTabView";

export class OrderList extends AutogenLayoutOrderList {
    override onLoad() {
        this.textboxId.addKeyUpListener(event => {
            if (event.domEvent != null && event.key === Keys.ENTER && this.textboxId.text?.length > 0)
                this.mainDataSource.search(this.mainDataSource.getDataboundValues(DataSourceMode.SEARCH, null, true)).finally(() => {
                    if (!StringUtil.isEmptyString(window.location.search)) // remove param ids from url if needed.
                        Navigation.pseudoNavigateTo(window.location.pathname, null)
                });
        });
        this.textboxReferenceNumbers.addKeyUpListener(event => {
            if (event.domEvent != null && event.key === Keys.ENTER && this.textboxReferenceNumbers.text?.length > 0)
                this.mainDataSource.search(this.mainDataSource.getDataboundValues(DataSourceMode.SEARCH, null, true)).finally(() => {
                    if (!StringUtil.isEmptyString(window.location.search)) // remove param ids from url if needed.
                        Navigation.pseudoNavigateTo(window.location.pathname, null)
                });
        });
        const LME_USER_FILTER = event => { event.filter.web_user_type = "U" };
        this.textboxEnteredUserId.addBeforeLookupModelSearchListener(LME_USER_FILTER);
        const urlProp = UrlUtil.getPropsFromUrl(window.location.search)
        if (urlProp != null && urlProp.ids != null) {
            this.mainDataSource.search({ ids: urlProp.ids });
        } else if (urlProp != null && urlProp.manifest_id != null) {
            this.mainDataSource.search({ manifest_id: urlProp.manifest_id });
        }
        this.citystateStopOriginCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateStopDestCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateStopOriginCityId.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateStopDestCityId.textCity.forcedCase = ForcedCase.UPPER;
        this.switchCheckHazmat.allowIndeterminate = true;
        this.switchCheckHazmat.checked = undefined;
        this.switchIntercompany.allowIndeterminate = true;
        this.switchIntercompany.checked = undefined;
        this.switchInvoiced.allowIndeterminate = true;
        this.switchInvoiced.checked = undefined;
        const partialShipmentEnabled = LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"];
        this.textboxOrderMode.visible = partialShipmentEnabled === "Y"
            || (LMECompanySettings.getSingleton().isAssetLtl() && GeneralSettings.hasEarlyAdoptersAccess());
        if (partialShipmentEnabled === "Y")
            this.textboxId.caption = "Order/Manifest ID"
        this.checkboxPreloaded.allowIndeterminate = true;
        this.checkboxPreloaded.checked = undefined;
        this.addLayoutLoadListener(() => {
            this.textboxId.focus();
        });

        this.textboxOriginLocation.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => 
            this.searchStopSelection("origin", this.panelShipperLocation, event));
        this.textboxDestLocation.addLookupModelSelectionListener(event => 
            this.searchStopSelection("consignee", this.panelConsigneeLocation, event)
        );

        this.textboxStopOriginLocationId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) =>
            this.searchStopPopulateSelection("origin", this.panelShipperLocation, event));
        this.textboxStopDestLocationId.addLookupModelSelectionListener(event =>
            this.searchStopPopulateSelection("consignee", this.panelConsigneeLocation, event)
        );
        this.panelOrderListContainer.rows[0].style.height = "100%"; // pretty nasty hack to make the vertical scroll bar behave
        this.tableOrders.overrideUrlParams = (selectedRow: TableRow) => {
            return { key: selectedRow.data.get("id"), showVoid: true };
        }
        this.tableOrders.rowProps = {
            onDblClick: (event) => this.tableRowOnDblClick(event)
        };
    }

    styleTab(tab: Tab) {
        tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
        tab.headingLabel.setProps({ color: "McLeodPrimary", fontSize: "xlarge", fontBold: true });
    }

    tableOrdersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (tableRow.data != null) {
            const modelRow = tableRow.data;
            const labelMultipleMoves: Label = tableRow.findComponentById("labelImageMoveCount") as Label;
            if (labelMultipleMoves != null) {
                labelMultipleMoves.visible = modelRow.get("movement_count") > 1;
                labelMultipleMoves.tooltip = `Multiple movements (${modelRow.get("movement_count")})`;
            }
            const labelCustomerName: Label = tableRow.findComponentById("labelCustomerName") as Label
            if (labelCustomerName != null) {
                labelCustomerName.quickInfoOverrideKeyValue = modelRow.get("customer_id");
            }
            const imageOnHold: Label = tableRow.findComponentById("imageOnHold") as Label;
            if (imageOnHold != null) {
                imageOnHold.visible = modelRow.getBoolean("on_hold", false);
                imageOnHold.tooltip = modelRow.get("hold_reason");
            }
            const labelStops: Label = tableRow.findComponentById("labelStops") as Label;
            if (labelStops != null) {
                labelStops.applyLinkStyle();
            }
            const labelTotalCharge = tableRow.findComponentById("labelTotalCharge") as Component
            if (labelTotalCharge != null) {
                CommonTooltips.setTooltipContentTextboxes(labelTotalCharge, () => [
                    {
                        caption: "Freight Charge",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("freight_charge"))
                    },
                    {
                        caption: "Other Charges",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("otherchargetotal"))
                    }]
                );
            }

            const movePay = tableRow.findComponentById("labelMovePay") as Component
            if (movePay != null) {
                CommonTooltips.setTooltipContentTextboxes(movePay, () => [
                    {
                        caption: "Carrier Pay",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("move_pay"))
                    },
                    {
                        caption: "Pay Adjustments",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("other_pay"))
                    }]
                )
            }
        }
    }

    textboxTractorFleetIdBeforeLookupModelSearch(event) {
        event.filter.is_tractor = 'Y';
    }

    textboxOrderStatusOnChange() {
        if (this.textboxOrderStatus.selectedItems?.some(value => ["D", "P"].includes(value.value)) != true) {
            this.textboxTractorFleetId.enabled = false;
            this.textboxTractorFleetId.clear();
            this.textboxTractorFleetId.tooltipDynamicContent = () => {
                return "This option is only available for orders that are in progress or delivered";
            }
        } else {
            this.textboxTractorFleetId.enabled = true;
        }
    }

    /** This is an event handler for the beforeExecution event of sourceOrdersList. */
    sourceOrdersListBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            const orderId = event.filter?.id;
            if (LMEGeneralSettings.getSingleton().getDispatchControl()["enable_manifesting"] === "Y"
                && !StringUtil.isEmptyString(orderId) && (orderId?.startsWith("M") || orderId?.startsWith("m"))) {
                event.filter.manifest_id = orderId;
                event.filter.id = null;
            }
        }
    }

    labelStopsOnClick(event: ClickEvent) {
        const labelStops = event.target as Label;
        const tableRow: TableRow = TableRow.getContainingTableRow(labelStops);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;

        PlanningUtil.showMultiStopSlideout(
            labelStops,
            true,
            row.get("id"),
            async () => {
                await tableRow.table.resolveRowEdit(row, row);
            }
        );
    }

    switchOnChange() {
        this.textboxInvoiceNumber.visible = this.switchInvoiced.checked === true;
        this.textboxInvoiceDate.visible = this.switchInvoiced.checked === true;
    }

    searchStopPopulateSelection(stopType: "origin" | "consignee", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            this.sourceOrdersList.searchRow.setValues({
                [`stop_${stopType}.address`]: selection.getString("address1", null),
                [`stop_${stopType}.address2`]: selection.getString("address2", null),
                [`stop_${stopType}.city_id`]: selection.getString("city_id", null),
                [`stop_${stopType}.city_name`]: selection.getString("city_name", null),
                [`stop_${stopType}.location_id`]: selection.getString("id", null),
                [`stop_${stopType}.location_name`]: selection.getString("name", null),
                [`stop_${stopType}.state`]: selection.getString("state", null),
                [`stop_${stopType}.zip_code`]: selection.getString("zip_code", null),
            });
        }
        this.searchStopSelection(stopType, targetPanel, event);
    }

    searchStopSelection(stopType: "origin" | "consignee", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            if (!selection.isNull("state")) {
                this.sourceOrdersList.searchRow.setLookupModelData(`stop_${stopType}.state`, { "id": selection.getString("state", null) });
            }
        }
        targetPanel.displayComponentData(this.sourceOrdersList.searchRow, null, 0);
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        if (this.tableOrders.detailLayout === "lme/dispatch/OrderTabView") {
            OrderTabView.slideUp({key: tableRow.data.get("id"), additionalProps: {showVoid: true}, newTab: true});
        }
        else {
            this.tableOrders.crudManager.showDetailFromClick(event);
        }
    }
}
